import { useCallback, useEffect, useRef, useState } from "react";
import { getScheduleAuditLogs } from "../../../../redux/features/scheduleV3";
import type { ScheduleAuditLog, ScheduleAuditLogsMeta, ScheduleAuditLogsRequest } from "../../../../types";

export type AuditLogsQuery = Omit<ScheduleAuditLogsRequest, "page" | "limit">;

interface State {
    logs: ScheduleAuditLog[];
    meta: ScheduleAuditLogsMeta | null;
    isLoading: boolean;
    isLoadingMore: boolean;
    error: string;
}

const INITIAL: State = { logs: [], meta: null, isLoading: false, isLoadingMore: false, error: "" };

/**
 * Loads schedule audit logs for `query` (page 1) and appends further pages via `loadMore`.
 * Pass `null` to stay idle. A change of `query` content restarts from page 1; stale responses are ignored.
 */
export const useScheduleAuditLogs = (query: AuditLogsQuery | null, limit = 50) => {
    const [state, setState] = useState<State>(INITIAL);
    const requestId = useRef(0);
    const queryKey = query ? JSON.stringify(query) : null;

    const fetchPage = useCallback(
        async (page: number) => {
            if (!queryKey) return;
            const id = ++requestId.current;
            const append = page > 1;
            setState((prev) => ({
                ...prev,
                isLoading: !append,
                isLoadingMore: append,
                error: "",
                ...(append ? {} : { logs: [], meta: null }),
            }));
            try {
                const response = await getScheduleAuditLogs({ ...JSON.parse(queryKey), page, limit });
                if (id !== requestId.current) return;
                if (!response.status) {
                    setState((prev) => ({
                        ...prev,
                        isLoading: false,
                        isLoadingMore: false,
                        error: response.message || "Failed to load audit logs",
                    }));
                    return;
                }
                const rows = Array.isArray(response.data) ? response.data : [];
                setState((prev) => ({
                    logs: append ? [...prev.logs, ...rows] : rows,
                    meta: response.meta ?? null,
                    isLoading: false,
                    isLoadingMore: false,
                    error: "",
                }));
            } catch (err: any) {
                if (id !== requestId.current) return;
                setState((prev) => ({
                    ...prev,
                    isLoading: false,
                    isLoadingMore: false,
                    error: err?.message || "Failed to load audit logs",
                }));
            }
        },
        [queryKey, limit]
    );

    useEffect(() => {
        if (!queryKey) {
            requestId.current += 1;
            setState(INITIAL);
            return;
        }
        fetchPage(1);
        return () => {
            requestId.current += 1;
        };
    }, [queryKey, fetchPage]);

    const loadMore = useCallback(() => {
        const nextPage = (state.meta?.page ?? 1) + 1;
        if (state.meta?.has_more && !state.isLoading && !state.isLoadingMore) fetchPage(nextPage);
    }, [state.meta, state.isLoading, state.isLoadingMore, fetchPage]);

    const refresh = useCallback(() => fetchPage(1), [fetchPage]);

    return { ...state, loadMore, refresh };
};
